/* eslint-disable ember/no-runloop */
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismMessage } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import type * as Highcharts from 'highcharts';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelNetRevenueSkeleton from 'lumen/components/hotel/net-revenue/skeleton';
import HotelNetRevenueStayDateChartTooltip from 'lumen/components/hotel/net-revenue/stay-date-chart/tooltip';
import {
  HALF_DAY_IN_MS,
  THREE_DAYS_IN_MS,
} from 'lumen/utils/constants/general-constants';
import {
  STAY_DATE_GRANULARITIES,
  type StayDateGranularity,
} from 'lumen/utils/constants/net-revenue-constants';
import type {
  StayDateData,
  StayDatePoint,
} from 'lumen/utils/net-revenue/types';

import styles from './stay-date-chart.module.css';

const TOOLTIP_DUMMY_ELEMENT_ID = 'stay-date-chart-tooltip-dummy';

interface HotelNetRevenueStayDateChartSignature {
  Args: {
    currencyCode: string;
    granularity: StayDateGranularity;
    isError: boolean;
    isLoading: boolean;
    stayDateData: StayDateData | undefined;
  };
}

export default class HotelNetRevenueStayDateChart extends Component<HotelNetRevenueStayDateChartSignature> {
  @service declare intl: Services['intl'];

  @tracked tooltipPoint: StayDatePoint | null = null;

  TOOLTIP_DUMMY_ELEMENT_ID = TOOLTIP_DUMMY_ELEMENT_ID;

  get chartModel(): Highcharts.SeriesOptionsType[] {
    const data = this.args.stayDateData;
    if (!data?.points.length) {
      return [];
    }

    const series: Highcharts.SeriesOptionsType[] = [
      {
        name: this.intl.t('net_revenue.net_room_revenue'),
        type: 'column',
        stack: 'current',
        data: data.points.map((p) => [dayjs(p.label).valueOf(), p.netRevenue]),
        color: 'var(--prism-color-chart-accent-primary-figure-default)',
      },
    ];

    if (data.comparisonLabel) {
      series.push({
        name: 'netRevenueComparison',
        type: 'column',
        stack: 'comparison',
        data: data.points.map((p) => [
          dayjs(p.label).valueOf(),
          p.comparison?.netRevenue ?? null,
        ]),
        color: 'var(--prism-color-chart-sequential-info-03)',
      });
    }

    series.push({
      name: this.intl.t('net_revenue.cost'),
      type: 'column',
      stack: 'current',
      data: data.points.map((p) => [dayjs(p.label).valueOf(), p.cost]),
      color: 'var(--prism-color-chart-status-figure-critical-normal)',
    });

    if (data.comparisonLabel) {
      series.push({
        name: 'costComparison',
        type: 'column',
        stack: 'comparison',
        data: data.points.map((p) => [
          dayjs(p.label).valueOf(),
          p.comparison?.cost ?? null,
        ]),
        color: 'var(--prism-color-chart-sequential-critical-03)',
      });
    }

    series.push({
      name: this.intl.t('general.occupancy'),
      type: 'line',
      yAxis: 1,
      dashStyle: 'Solid',
      marker: { enabled: false },
      data: data.points.map((p) => [
        dayjs(p.label).valueOf(),
        p.occupancy * 100,
      ]),
      color: 'var(--prism-color-chart-accent-secondary-figure-subdued)',
    });

    return series;
  }

  get chartOptions(): Highcharts.Options {
    const comparisonSeriesLabel = this.args.stayDateData?.comparisonLabel ?? '';
    const currencyCode = this.args.currencyCode;
    const intl = this.intl;
    const points = this.args.stayDateData?.points ?? [];
    const granularity = this.args.granularity;
    const isDay = granularity === STAY_DATE_GRANULARITIES.DAY;
    const formatStayDateAxisLabel = this.#formatStayDateAxisLabel.bind(this);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    const plotBands: Highcharts.XAxisPlotBandsOptions[] = [];
    if (isDay) {
      points.forEach((point) => {
        const day = dayjs(point.label);
        const dayOfWeek = day.day();
        if (dayOfWeek === 5 || dayOfWeek === 6) {
          plotBands.push({
            from: day.valueOf() - HALF_DAY_IN_MS,
            to: day.valueOf() + HALF_DAY_IN_MS,
            color: 'var(--prism-color-elevation-canvas)',
          });
        }
      });
    }

    return {
      chart: { height: 358 },
      xAxis: {
        type: 'datetime' as const,
        ...(isDay ? { minTickInterval: THREE_DAYS_IN_MS } : {}),
        labels: {
          formatter(): string {
            return formatStayDateAxisLabel(this.value as number, granularity);
          },
        },
        plotBands,
      },
      yAxis: [
        {
          title: undefined,
          reversedStacks: false,
          tickAmount: 5,
          labels: {
            formatter(): string {
              return intl.formatNumber(Number(this.value), {
                style: 'currency',
                currency: currencyCode,
                notation: 'compact',
                maximumFractionDigits: 0,
              });
            },
          },
        },
        {
          title: { text: this.intl.t('general.occupancy') },
          opposite: true,
          min: 0,
          // `softMax` keeps the axis at 0-100% for normal data (same ticks
          // as before), but lets Highcharts extend it automatically, rather
          // than clipping the line, on the rare day occupancy exceeds 100%
          // (overbooking).
          softMax: 100,
          tickAmount: 5,
          labels: {
            formatter(): string {
              return `${this.value}%`;
            },
          },
        },
      ],
      plotOptions: {
        column: {
          stacking: 'normal' as const,
          pointWidth: 8,
          pointPadding: 0,
          groupPadding: 0.3,
        },
      },
      legend: {
        labelFormatter(): string {
          const labels: Record<string, string> = {
            netRevenueComparison: comparisonSeriesLabel,
            costComparison: comparisonSeriesLabel,
          };
          const series = this as unknown as Highcharts.Series;
          return labels[series.name] ?? series.name;
        },
      },
      tooltip: {
        backgroundColor: 'transparent',
        borderWidth: 0,
        outside: true,
        padding: 0,
        shadow: false,
        shared: true,
        useHTML: true,
        formatter(): string {
          const point = self.pointsByTimestamp.get(this.x) ?? null;
          begin();
          self.tooltipPoint = point;
          end();
          if (!point) {
            return '';
          }
          return (
            document.getElementById(TOOLTIP_DUMMY_ELEMENT_ID)?.innerHTML ?? ''
          );
        },
      },
    };
  }

  get comparisonLabel(): string | null {
    return this.args.stayDateData?.comparisonLabel ?? null;
  }

  get isEmpty(): boolean {
    return !this.args.stayDateData?.points.length;
  }

  @cached
  get pointsByTimestamp(): Map<number, StayDatePoint> {
    const map = new Map<number, StayDatePoint>();
    this.args.stayDateData?.points.forEach((point) => {
      map.set(dayjs(point.label).valueOf(), point);
    });
    return map;
  }

  #formatStayDateAxisLabel(
    timestamp: number,
    granularity: StayDateGranularity,
  ): string {
    const date = dayjs(timestamp);
    if (granularity === STAY_DATE_GRANULARITIES.WEEK) {
      return this.intl.t('general.week_of', { date: date.format('D MMM') });
    }
    if (granularity === STAY_DATE_GRANULARITIES.MONTH) {
      return date.format('MMM YYYY');
    }
    return date.format('D MMM YYYY');
  }

  <template>
    <div data-test-id="stay-date-chart">
      {{#if @isLoading}}
        <div
          class="{{styles.card}} h-[280px]"
          data-test-id="stay-date-chart-skeleton"
        >
          <HotelNetRevenueSkeleton class="rounded-200 h-200 w-1/5" />
        </div>
      {{else if @isError}}
        <div
          class="{{styles.card}} {{styles.card-message}}"
          data-test-id="stay-date-chart-error"
        >
          <PrismMessage
            @illustration="100x100_critical"
            @title={{t "net_revenue.stay_date_chart_error"}}
          />
        </div>
      {{else if this.isEmpty}}
        <div
          class="{{styles.card}} {{styles.card-message}}"
          data-test-id="stay-date-chart-empty"
        >
          <PrismMessage
            @illustration="100x100_info"
            @title={{t "net_revenue.stay_date_chart_empty"}}
          />
        </div>
      {{else}}
        <div class={{styles.card}} data-test-id="stay-date-chart-container">
          <GeneralChartsOtaChart
            @chartOptions={{this.chartOptions}}
            @model={{this.chartModel}}
          />
        </div>

        <div hidden id={{this.TOOLTIP_DUMMY_ELEMENT_ID}}>
          {{#if this.tooltipPoint}}
            <HotelNetRevenueStayDateChartTooltip
              @comparisonLabel={{this.comparisonLabel}}
              @currencyCode={{@currencyCode}}
              @granularity={{@granularity}}
              @point={{this.tooltipPoint}}
            />
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
